/**
 * Risk Matrix Renderer
 * 
 * Renders 2D matrix (Likelihood vs Impact).
 * Color-codes risk zones and positions items.
 */

import type { VisualizationRenderer } from '../registry.js';
import { designTokens, escapeHtml } from '../../../core/design-system.js';

export const renderRiskMatrix: VisualizationRenderer = (data, meta) => {
  // Expected data format: array of { label: string, likelihood: number, impact: number }
  if (!Array.isArray(data) || data.length === 0) {
    return renderEmptyState('No data to display');
  }

  // Normalize data format
  const items = data.map(item => {
    const label = item.label || item.name || item.risk || 'Untitled';
    const likelihood = item.likelihood !== undefined ? Number(item.likelihood) : 0;
    const impact = item.impact !== undefined ? Number(item.impact) : 0;
    
    return {
      label: String(label),
      likelihood: Math.max(0, Math.min(1, likelihood)), // Clamp to 0-1
      impact: Math.max(0, Math.min(1, impact)) // Clamp to 0-1
    };
  });

  // Matrix dimensions
  const size = 500;
  const padding = 20;
  const quadrantSize = (size - padding * 2) / 2;
  const centerX = padding + quadrantSize;
  const centerY = padding + quadrantSize;

  // Risk zone colors (from low to high risk)
  const riskZones = [
    { id: 'low', color: 'rgba(100, 200, 100, 0.2)', border: 'rgba(100, 200, 100, 0.4)', label: 'Low Risk' },
    { id: 'medium-low', color: 'rgba(200, 200, 100, 0.2)', border: 'rgba(200, 200, 100, 0.4)', label: 'Medium-Low Risk' },
    { id: 'medium-high', color: 'rgba(255, 200, 100, 0.2)', border: 'rgba(255, 200, 100, 0.4)', label: 'Medium-High Risk' },
    { id: 'high', color: 'rgba(255, 100, 100, 0.2)', border: 'rgba(255, 100, 100, 0.4)', label: 'High Risk' }
  ];

  // Generate risk zone backgrounds (gradient from low to high)
  const zoneElements = [
    // Low risk (bottom-left)
    `<rect x="${padding}" y="${centerY}" width="${quadrantSize}" height="${quadrantSize}" fill="${riskZones[0].color}" stroke="${riskZones[0].border}" stroke-width="1" />`,
    // Medium-low risk (bottom-right)
    `<rect x="${centerX}" y="${centerY}" width="${quadrantSize}" height="${quadrantSize}" fill="${riskZones[1].color}" stroke="${riskZones[1].border}" stroke-width="1" />`,
    // Medium-high risk (top-left)
    `<rect x="${padding}" y="${padding}" width="${quadrantSize}" height="${quadrantSize}" fill="${riskZones[2].color}" stroke="${riskZones[2].border}" stroke-width="1" />`,
    // High risk (top-right)
    `<rect x="${centerX}" y="${padding}" width="${quadrantSize}" height="${quadrantSize}" fill="${riskZones[3].color}" stroke="${riskZones[3].border}" stroke-width="1" />`
  ].join('');

  // Generate axis labels
  const axisLabels = `
    <text
      x="${size / 2}"
      y="${size - 5}"
      text-anchor="middle"
      font-size="${designTokens.fontSize.sm}"
      font-weight="600"
      fill="${designTokens.contentColor}"
    >Likelihood →</text>
    <text
      x="10"
      y="${size / 2}"
      text-anchor="middle"
      font-size="${designTokens.fontSize.sm}"
      font-weight="600"
      fill="${designTokens.contentColor}"
      transform="rotate(-90 10 ${size / 2})"
    >Impact →</text>
  `;

  // Generate axis lines
  const axisLines = `
    <line
      x1="${centerX}"
      y1="${padding}"
      x2="${centerX}"
      y2="${size - padding}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
      opacity="0.5"
    />
    <line
      x1="${padding}"
      y1="${centerY}"
      x2="${size - padding}"
      y2="${centerY}"
      stroke="${designTokens.contentColor}"
      stroke-width="2"
      opacity="0.5"
    />
  `;

  // Position items on matrix
  const itemElements = items.map((item, index) => {
    // Map impact (0-1) to Y position (bottom to top)
    const y = size - padding - (item.impact * quadrantSize * 2);
    // Map likelihood (0-1) to X position (left to right)
    const x = padding + (item.likelihood * quadrantSize * 2);
    
    // Determine risk level for color
    const riskScore = (item.likelihood + item.impact) / 2;
    let itemColor = 'rgba(255, 255, 255, 0.8)';
    if (riskScore >= 0.75) itemColor = 'rgba(255, 100, 100, 0.9)';
    else if (riskScore >= 0.5) itemColor = 'rgba(255, 200, 100, 0.9)';
    else if (riskScore >= 0.25) itemColor = 'rgba(200, 200, 100, 0.9)';
    else itemColor = 'rgba(100, 200, 100, 0.9)';

    const tooltipId = `risk-tooltip-${Date.now()}-${index}`;
    
    return `
      <g>
        <circle
          cx="${x}"
          cy="${y}"
          r="8"
          fill="${itemColor}"
          stroke="${designTokens.contentColor}"
          stroke-width="1.5"
          style="cursor: pointer;"
          data-label="${escapeHtml(item.label)}"
          data-likelihood="${item.likelihood.toFixed(2)}"
          data-impact="${item.impact.toFixed(2)}"
          onmouseenter="
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'block';
          "
          onmouseleave="
            const tooltip = document.getElementById('${tooltipId}');
            if (tooltip) tooltip.style.display = 'none';
          "
        />
        <g id="${tooltipId}" style="display: none; pointer-events: none;">
          <rect
            x="${x + 12}"
            y="${y - 30}"
            width="150"
            height="50"
            fill="rgba(30, 32, 40, 0.95)"
            stroke="${designTokens.borderColor}"
            stroke-width="1"
            rx="4"
          />
          <text
            x="${x + 17}"
            y="${y - 12}"
            font-size="${designTokens.fontSize.xs}"
            font-weight="600"
            fill="${designTokens.contentColor}"
          >${escapeHtml(item.label)}</text>
          <text
            x="${x + 17}"
            y="${y + 2}"
            font-size="${designTokens.fontSize.xs}"
            fill="${designTokens.contentColorMuted}"
          >Likelihood: ${(item.likelihood * 100).toFixed(0)}%</text>
          <text
            x="${x + 17}"
            y="${y + 16}"
            font-size="${designTokens.fontSize.xs}"
            fill="${designTokens.contentColorMuted}"
          >Impact: ${(item.impact * 100).toFixed(0)}%</text>
        </g>
      </g>
    `;
  }).join('');

  return `
    <div style="width: 100%;">
      <div style="
        width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
      ">
        <svg
          width="${size}"
          height="${size}"
          style="
            display: block;
            max-width: 100%;
            height: auto;
          "
        >
          ${zoneElements}
          ${axisLines}
          ${axisLabels}
          ${itemElements}
        </svg>
      </div>
    </div>
  `;
};

function renderEmptyState(message: string): string {
  return `
    <div style="
      padding: ${designTokens.padding.xl};
      text-align: center;
      color: ${designTokens.contentColorMuted};
      font-size: ${designTokens.fontSize.sm};
    ">${escapeHtml(message)}</div>
  `;
}


